import { UserStatus, type User } from "./user.js";
import { matchesLegacyPassword, needsPasswordUpgrade, verifyPassword } from "./password-hash.js";

/**
 * REGRA ÚNICA DE AUTENTICAÇÃO POR CREDENCIAL (login/e-mail + senha).
 *
 * Vale para qualquer ambiente sem backend HTTP (teste local, testes
 * automatizados): identifica o usuário pelos identificadores OFICIAIS —
 * login, username ou e-mail, NUNCA pelo nome completo (nome não é
 * identificador de acesso e pode repetir) —, confere a senha e só deixa
 * passar usuário ATIVO. Perfil/Role não participa: RBAC é depois da
 * autenticação.
 *
 * A senha é conferida pelo mecanismo oficial (`password-hash.ts`, PBKDF2-
 * SHA-256 com sal por usuário). Credenciais gravadas antes dele (texto puro
 * em `password`, ou `hash_<senha>` em `passwordHash`) continuam entrando uma
 * última vez e são sinalizadas em `needsUpgrade` — quem autentica regrava a
 * senha no formato oficial na mesma operação, de modo que o formato antigo
 * desaparece pelo uso, sem ninguém perder o acesso.
 */

export type CredentialFailure = "NO_USERS" | "INVALID_CREDENTIALS" | "USER_INACTIVE" | "NO_PASSWORD";

export interface CredentialAuthentication {
  readonly user: User | null;
  readonly failure: CredentialFailure | null;
  /** Autenticou por credencial antiga: a senha precisa ser regravada em formato seguro. */
  readonly needsUpgrade?: boolean;
}

/** Identificadores oficiais de acesso: login, username e e-mail (jamais o nome). */
export function findUserByIdentifier(users: readonly User[], identifier: string): User | null {
  const term = (identifier || "").trim().toLowerCase();
  if (!term) return null;
  return users.find(u =>
    (u.login && u.login.trim().toLowerCase() === term) ||
    (u.username && u.username.trim().toLowerCase() === term) ||
    (u.email && u.email.trim().toLowerCase() === term)
  ) || null;
}

/** Confere a senha pelo formato oficial e, só então, pelo formato antigo. */
export async function passwordMatches(user: User, password: string): Promise<boolean> {
  if (await verifyPassword(password, user.passwordHash)) return true;
  return matchesLegacyPassword(password, user);
}

export function isActiveUser(user: User): boolean {
  return user.status === UserStatus.Active && !user.deletedAt;
}

export async function authenticateWithCredentials(users: readonly User[], identifier: string, password: string): Promise<CredentialAuthentication> {
  if (!users || users.length === 0) return { user: null, failure: "NO_USERS" };
  if (!identifier || !identifier.trim() || !password) return { user: null, failure: "INVALID_CREDENTIALS" };
  const user = findUserByIdentifier(users, identifier);
  if (!user) return { user: null, failure: "INVALID_CREDENTIALS" };
  if (!user.password && !user.passwordHash) return { user: null, failure: "NO_PASSWORD" };
  if (!(await passwordMatches(user, password))) return { user: null, failure: "INVALID_CREDENTIALS" };
  // Situação (ativo/inativo) é checada DEPOIS da senha: um usuário inativo não
  // deve ser revelado a quem nem sabe a credencial.
  if (!isActiveUser(user)) return { user: null, failure: "USER_INACTIVE" };
  return { user, failure: null, needsUpgrade: needsPasswordUpgrade(user) };
}

/** Mensagens de tela — genéricas para credencial (não revelam qual campo errou). */
export const CREDENTIAL_FAILURE_MESSAGES: Record<CredentialFailure, string> = {
  NO_USERS: "Nenhum usuário cadastrado. Contate o administrador.",
  INVALID_CREDENTIALS: "Login ou senha inválidos.",
  USER_INACTIVE: "Usuário bloqueado ou inativo. Contate o administrador.",
  NO_PASSWORD: "Usuário sem senha definida. Contate o administrador."
};
